import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import CurrencyInput from '../../ui/inputs/CurrencyInput';
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox';
import Textarea from '../../ui/inputs/Textarea';
import { Star, X } from 'lucide-react';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { useToast } from '../../ui/toast/ToastContext';
import { useConfirm } from '../../ui/confirm/ConfirmContext';
import CustomButton from '../../ui/buttons/CustomButton';

const ClientTransactionModal = ({
  clientId,
  transactionId = null,
  scope,
  onClose,
  onSuccess,
  transactionType,
  canWrite = true,
}) => {
  const isEdit = !!transactionId;
  const { addToast } = useToast();

  const [form, setForm] = useState({
    description: '',
    amount: '',
    transactionType: transactionType || 'active_income',
  });

  const confirm = useConfirm();

  // Carrega dados da transação (modo edição)
  useEffect(() => {
    if (isEdit) {
      fetch(`/app/planner/client/${clientId}/dashboard/investment_profile/transactions/${transactionId}?scope=${scope}`)
        .then((res) => res.json())
        .then((data) => setForm(camelizeKeys(data)))
        .catch((err) => {
          console.error('Erro ao carregar transação', err);
          addToast({ type: 'error', message: 'Erro ao carregar dados da transação' });
        });
    }
  }, [isEdit, clientId, transactionId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'EXCLUIR TRANSAÇÃO?',
      description: `Tem certeza que deseja excluir a transação?`,
      confirmText: 'EXCLUIR',
    });

    if (!ok) return;

    try {
      const res = await fetchWithCsrf(
        `/app/planner/client/${clientId}/dashboard/investment_profile/transactions/${transactionId}`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao excluir a transação');
      }

      addToast({ type: 'success', message: 'Transação excluída com sucesso!' });
      onSuccess();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = snakecaseKeys({ transaction: form });

    const url = isEdit
      ? `/app/planner/client/${clientId}/dashboard/investment_profile/transactions/${transactionId}?scope=${scope}`
      : `/app/planner/client/${clientId}/dashboard/investment_profile/transactions?scope=${scope}`;

    const method = isEdit ? 'PATCH' : 'POST';
    const successMsg = isEdit ? 'Transação atualizada com sucesso!' : 'Transação cadastrada com sucesso!';

    try {
      const res = await fetchWithCsrf(url, {
        method,
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao salvar transação');
      }

      onClose();
      addToast({ type: 'success', message: successMsg });
      onSuccess();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const titlesMap = {
    active_income: 'Receita Ativa',
    passive_income: 'Receita Passiva',
    expense: 'Despesa',
  };

  const baseTitle = titlesMap[form.transactionType] || 'Transação';

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <Star size={24} className="text-tealMedium" strokeWidth={1.25} />
            {isEdit ? `Editar ${baseTitle}` : `Cadastrar ${baseTitle}`}
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <Input
            name="description"
            label="Descrição"
            value={form.description}
            onChange={handleChange}
            disabled={!canWrite}
          />

          <CurrencyInput
            name="amount"
            label="Valor"
            value={form.amount}
            onChange={handleChange}
            required
            disabled={!canWrite}
          />

          {canWrite && (
            <div className="flex justify-between items-center md:col-span-2">
              {isEdit ? (
                <span className="text-lg text-redSoft font-semibold underline cursor-pointer" onClick={handleDelete}>
                  EXCLUIR
                </span>
              ) : (
                <div></div>
              )}

              <CustomButton type="submit">
                {isEdit ? 'SALVAR ALTERAÇÕES' : `ADICIONAR ${baseTitle.toUpperCase()}`}
              </CustomButton>
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
};

export default ClientTransactionModal;
